<template>
  <div class="modern-finalize-wrapper">
    <div class="finalize-container">
      <!-- Header Section -->
      <div class="verify-header">
        <div class="verify-title-container">
          <div class="verify-icon">
            <svg 
              width="32" 
              height="32" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
          </div>
          <h1 class="verify-title">Verify Your Details</h1>
        </div>
        <p class="verify-subtitle">Please review all information before final submission. Make sure all details are correct.</p>
      </div>

      <!-- Receipt Verification Section -->
      <div class="receipt-section">
        <div class="section-header">
          <svg 
            class="section-icon red-icon" 
            width="20" 
            height="20" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
          </svg>
          <h3>Please verify your receipt details</h3>
        </div>
        <div class="receipt-grid">
          <div class="receipt-item">
            <label class="receipt-label">Settlement Date & Time</label>
            <p class="receipt-value">{{ settledDate }}</p>
          </div>
          
          <div class="receipt-item">
            <label class="receipt-label">Receipt No</label>
            <p class="receipt-value">{{ invoiceNo }}</p>
          </div>
          
          <div class="receipt-item">
            <label class="receipt-label">Grand Total</label>
            <p class="receipt-value amount">RM {{ grandTotal }}</p>
          </div>
        </div>
      </div>

      <!-- Customer Information Section -->
      <div v-if="customerInfo">
        <!-- Personal Information Card -->
        <div class="info-card">
          <div class="section-header">
            <svg 
              class="section-icon red-icon" 
              width="20" 
              height="20" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle 
                cx="12" 
                cy="7" 
                r="4"/>
            </svg>
            <h3>Personal Information</h3>
          </div>
          <div class="info-content">
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Name:</span> 
                <span class="info-value">{{ customerInfo.name || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Email:</span> 
                <span class="info-value">{{ customerInfo.email || '-' }}</span>
              </div>
              <div class="info-item full-width">
                <span class="info-label">Contact:</span> 
                <span class="info-value">{{ customerInfo.contact_no || '-' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Address Information Card -->
        <div class="info-card">
          <div class="section-header">
            <svg 
              class="section-icon red-icon" 
              width="20" 
              height="20" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle 
                cx="12" 
                cy="10" 
                r="3"/>
            </svg>
            <h3>Address Information</h3>
          </div>
          <div class="info-content">
            <div class="info-grid">
              <div class="info-item full-width">
                <span class="info-label">Address:</span> 
                <span class="info-value">{{ customerInfo.address || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Address Line 2:</span> 
                <span class="info-value">{{ customerInfo.address_2 || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Address Line 3:</span> 
                <span class="info-value">{{ customerInfo.address_3 || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">City:</span> 
                <span class="info-value">{{ customerInfo.city || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">State:</span> 
                <span class="info-value">{{ customerInfo.state || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Country:</span> 
                <span class="info-value">{{ customerInfo.country || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Postal Code:</span> 
                <span class="info-value">{{ customerInfo.postal_zone || '-' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Identification & Tax Information Card -->
        <div class="info-card">
          <div class="section-header">
            <svg 
              class="section-icon red-icon" 
              width="20" 
              height="20" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <rect 
                x="1" 
                y="4" 
                width="22" 
                height="16" 
                rx="2" 
                ry="2"/>
              <line 
                x1="1" 
                y1="10" 
                x2="23" 
                y2="10"/>
            </svg>
            <h3>Identification & Tax Information</h3>
          </div>
          <div class="info-content">
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">ID Type:</span> 
                <span class="info-value">{{ customerInfo.idType || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">ID Number:</span> 
                <span class="info-value">{{ customerInfo.id_no || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">TIN:</span> 
                <span class="info-value">{{ customerInfo.enter_id || '-' }}</span>
              </div>
              <div 
                v-if="customerInfo.idType === 'BRN'" 
                class="info-item">
                <span class="info-label">SST Reg. No:</span> 
                <span class="info-value">{{ customerInfo.sst_reg_no || '-' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Email Section -->
      <div class="email-section">
        <div class="section-header">
          <svg 
            class="section-icon red-icon" 
            width="20" 
            height="20" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            viewBox="0 0 24 24">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          <h3>Soft Copy Delivery</h3>
        </div>

        <div class="email-form">
          <div class="form-group">
            <label class="modern-label">
              Enter your email to receive a Soft Copy
              <span class="required">*</span>
            </label>
            <div class="input-container">
              <svg 
                class="input-icon" 
                width="16" 
                height="16" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                viewBox="0 0 24 24">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <input
                v-model="softcopyEmail.email"
                :class="{ 'input-focused': softcopyEmail.email }"
                type="email"
                placeholder="ahmadali@example.com"
                class="modern-input"
                required
              >
            </div>
            <p class="help-text">
              <svg 
                width="14" 
                height="14" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                viewBox="0 0 24 24">
                <circle 
                  cx="12" 
                  cy="12" 
                  r="10"/>
                <path d="M9,12l2,2 4,-4"/>
              </svg>
              You will receive the e-invoice copy at this email address
            </p>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-section">
        <div class="action-buttons">
          <button 
            class="modern-btn secondary-btn" 
            @click="$emit('previous')">
            <svg 
              width="16" 
              height="16" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <polyline points="15,18 9,12 15,6"/>
            </svg>
            Back
          </button>
          <button 
            class="modern-btn primary-btn" 
            @click="submitData">
            Submit Request
            <svg 
              width="16" 
              height="16" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2';

export default {
    name: 'FinalizeRequest',
    props: {
        settledDate: {
            type: String,
            required: true,
        },
        invoiceNo: {
            type: String,
            required: true,
        },
        grandTotal: {
            type: String,
            required: true,
        },
        formData: {
            type: Object,
            default: () => ({}),
        },
        customerInfo: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            softcopyEmail: {
                email: '',
            },
        };
    },
    computed: {
        isFormValid() {
            const { email } = this.softcopyEmail;
            if (!email) return false;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        },
    },
    watch: {
        softcopyEmail: {
            deep: true,
            handler() {
                this.$emit('update-form-data', this.softcopyEmail);
            },
        },
    },
    methods: {
        submitData() {
            if (this.isFormValid) {
                this.$emit('submit-data', {
                    softcopy_email: this.softcopyEmail.email,
                });
                this.$emit('validate-data');
            } else {
                swal({
                    title: 'Warning',
                    text:
                        'Please enter a valid Email Address (e.g. example@mail.com)',
                    icon: 'warning',
                    confirmButtonColor: '#ef5350',
                    confirmButtonText: 'Close',
                });
            }
        },
    },
};
</script>

<style scoped>
.modern-finalize-wrapper {
    background: #f8fafc;
    min-height: 100%;
}

.finalize-container {
    padding: 24px;
    max-width: 600px;
    margin: 0 auto;
}

.receipt-section,
.email-section,
.customer-info-section {
    /* Added customer-info-section */
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
    overflow: hidden;
}

.section-header {
    display: flex;
    align-items: center;
    padding: 20px 24px;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
}

/* All section icons now red */
.section-icon,
.info-icon {
    /* Added info-icon */
    color: #ef5350;
    flex-shrink: 0;
}

.section-icon {
    margin-right: 12px;
}

.info-icon {
    /* Ensure info-icon itself is red and has margin */
    color: #ef5350;
    margin-right: 12px;
    flex-shrink: 0;
}

.section-header h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.receipt-grid {
    padding: 24px;
    display: grid;
    gap: 20px;
}

.receipt-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.receipt-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
}

.receipt-value {
    font-size: 1rem;
    color: #1e293b;
    margin: 0;
    padding: 8px 0;
}

.receipt-value.amount {
    font-size: 1.25rem;
    font-weight: 700;
    color: #ef5350;
}

.email-form {
    padding: 24px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.modern-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 4px;
}

.required {
    color: #ef5350;
}

.input-container {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 12px;
    color: #9ca3af;
    z-index: 1;
}

.modern-input {
    width: 100%;
    padding: 12px 16px 12px 40px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.2s ease;
    background: white;
    box-sizing: border-box;
}

.modern-input:focus {
    outline: none;
    border-color: #ef5350;
    box-shadow: 0 0 0 3px rgba(239, 83, 80, 0.1);
}

.modern-input.input-focused {
    border-color: #ef5350;
}

.help-text {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0;
    padding: 4px 0;
}

/* Customer Information Styles - Individual Cards */
.info-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
    overflow: hidden;
}

.info-content {
    padding: 24px;
}

.info-grid {
    display: grid;
    grid-template-columns: 1fr; /* Default for mobile */
    gap: 16px;
}

@media (min-width: 768px) {
    .info-grid {
        grid-template-columns: 1fr 1fr; /* Two columns for desktop */
    }
    .info-item.full-width {
        grid-column: 1 / -1; /* Span all columns */
    }
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 16px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.info-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #64748b; /* Gray label color */
    margin-bottom: 2px;
}

.info-value {
    font-size: 0.95rem;
    color: #1e293b;
    word-break: break-word;
    font-weight: 500;
}

.action-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 24px;
    position: sticky;
    bottom: 0;
}

.action-buttons {
    display: flex;
    gap: 12px;
    justify-content: space-between;
}

.modern-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 120px;
    justify-content: center;
}

.primary-btn {
    background: #ef5350;
    color: white;
    flex: 1;
}

.primary-btn:hover {
    background: #e53935;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 83, 80, 0.3);
}

.secondary-btn {
    background: white;
    color: #374151;
    border: 2px solid #e5e7eb;
}

.secondary-btn:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateY(-1px);
}

/* Header Section Styles */
.verify-header {
    text-align: center;
    padding: 32px 24px;
    margin-bottom: 32px;
}

.verify-title-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-bottom: 16px;
}

.verify-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: #ef5350;
    border-radius: 50%;
    color: white;
    flex-shrink: 0;
}

.verify-title {
    font-size: 2rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    letter-spacing: -0.025em;
}

.verify-subtitle {
    font-size: 1rem;
    color: #64748b;
    margin: 0;
    max-width: 500px;
    margin: 0 auto;
    line-height: 1.5;
}

@media (max-width: 768px) {
    .verify-header {
        padding: 24px 16px;
        margin-bottom: 24px;
    }

    .verify-title-container {
        flex-direction: column;
        gap: 12px;
    }

    .verify-icon {
        width: 40px;
        height: 40px;
    }

    .verify-title {
        font-size: 1.5rem;
        text-align: center;
    }

    .verify-subtitle {
        font-size: 0.875rem;
    }

    .finalize-container {
        padding: 16px;
    }

    .section-header {
        padding: 16px 20px;
    }

    .receipt-grid,
    .email-form,
    .info-content {
        /* Updated from customer-info-section to info-content */
        padding: 20px;
    }

    .info-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .action-section {
        padding: 20px;
    }

    .action-buttons {
        flex-direction: column;
    }

    .modern-btn {
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/e-invoice/portal/finalize-request.vue